/**
 * When a field's validation message is put on screen.
 *
 * The validators (field-validations.js) always decide whether a value is valid; this decides
 * whether the person should SEE that yet. Showing errors on a field someone only tabbed past,
 * or while they are still typing, reads as being told off for something they haven't done, so:
 *
 *  - typing never adds an error; it only updates or clears one that is already showing
 *  - leaving a field (blur) or picking an option shows the first failing message, but only on
 *    a field the person changed
 *  - "required" waits for Next: an empty field shows nothing until the step is submitted
 *  - Next ('submit') shows the first failing message on every field
 *
 * One message per field: the validators list every failing rule with "required" first, so the
 * first entry is the one that matters (an empty name is "required", not also "letters only").
 */

export const TRIGGERS = Object.freeze({
    INPUT: 'input',
    BLUR: 'blur',
    PICK: 'pick',
    SUBMIT: 'submit',
});

// Shown when the backend says an email that passed the format check can't receive mail.
export const EMAIL_UNDELIVERABLE_MESSAGE = '__email_undeliverable__';

const REQUIRED_MESSAGE = '__required_field__';
const REQUIRED_CHOICE_MESSAGE = '__required_choice__';
const CHOICE_FIELD_TYPES = ['radio', 'select', 'checkbox'];

export function isEmptyValue(value) {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    return false;
}

/**
 * The messages a field should show after a validation run.
 *
 * @param {Object} args
 * @param {string} args.trigger - one of TRIGGERS
 * @param {*} args.value - the value that was validated
 * @param {{isValid: boolean, messages?: string[]}} args.result - from fieldValidationHelper / addressFieldValidationHelper
 * @param {boolean} [args.isDirty] - the person changed this field on this step
 * @param {boolean} [args.revealed] - Next has been pressed on this step
 * @param {string[]} [args.shown] - the messages the field shows right now
 * @returns {string[]} zero or one message
 */
export function visibleErrors({ trigger, value, result, isDirty = false, revealed = false, shown = [] }) {
    const firstMessage = result?.isValid ? [] : (result?.messages ?? []).slice(0, 1);

    if (trigger === TRIGGERS.SUBMIT) {
        return firstMessage;
    }

    if (isEmptyValue(value) && !revealed) {
        return [];
    }

    const isShowing = Array.isArray(shown) && shown.length > 0;

    switch (trigger) {
        case TRIGGERS.INPUT:
            return isShowing ? firstMessage : [];
        case TRIGGERS.BLUR:
            return (isDirty || isShowing) ? firstMessage : [];
        case TRIGGERS.PICK:
            return firstMessage;
        default:
            return isShowing ? firstMessage : [];
    }
}

/**
 * "Please fill in this field" reads oddly on a set of options; say "choose" instead.
 */
export function messageForFieldType(message, fieldType) {
    if (message === REQUIRED_MESSAGE && CHOICE_FIELD_TYPES.includes(fieldType)) {
        return REQUIRED_CHOICE_MESSAGE;
    }
    return message;
}
